import type { ShopifyAsset, IShopifyPageAssigned } from '~/modules/shopify';
import type { IQueryThemePages } from '~/modules/apps';
import type { IThemePage } from '~/stores/themePage';
import type { IAssigned } from '~/stores/assigned';

import { deepCopy, parseAssignedTemplates, parseTemplateInfoBy } from '~/utils';
import { processAssetUnassigned } from '~/modules/campaign/helpers';
import { regexTemplateSupport } from '~/constants/templateRegex';
import { TAB_TEMPLATES } from '~/constants/template';
import { getAssetKey } from '~/presentation/common';
import { ProviderSource } from '~/types/template';

interface BuildAssetsWithAssignedParams {
  shopifyAssets: {
    templateGPV7: ShopifyAsset[];
    templateGPV6: ShopifyAsset[];
    templateShopify: ShopifyAsset[];
  };
  themePages: IThemePage[] | undefined;
  assignedData: IAssigned | undefined;
  shopURL: string;
  defaultThemePages: IQueryThemePages['defaultThemePages'];
}

const processAssets = ({
  shopifyAssets,
  themePages,
  assignedData,
  shopURL,
}: BuildAssetsWithAssignedParams): ShopifyAsset[] => {
  if (!shopifyAssets) return [];

  const allAssets = [
    ...shopifyAssets.templateGPV7,
    ...shopifyAssets.templateGPV6,
    ...shopifyAssets.templateShopify,
  ];

  return allAssets
    .map((asset) => {
      const checkMatch = asset.key?.match(regexTemplateSupport);
      if (!checkMatch) return false;

      const assetKey = getAssetKey(asset, themePages);
      const templateInfo = parseTemplateInfoBy({ templateSuffix: assetKey, themePages });
      if (!templateInfo) return asset;

      const templateAssigned = assignedData?.[templateInfo.type];
      const assigned = parseAssignedTemplates({
        assetKey,
        templateAssigned,
        shopURL,
      }) || {
        total: 0,
        url: '',
        data: [],
      };

      return { ...asset, assigned, id: asset.key };
    })
    .filter((item): item is ShopifyAsset => !!item);
};

const mergeUniqueData = (
  existingData: IShopifyPageAssigned['data'],
  newData: IShopifyPageAssigned['data'],
) => {
  const existingIds = new Set(existingData.map((item) => item.id));
  const uniqueNewData = newData.filter((item) => !existingIds.has(item.id));
  return [...existingData, ...uniqueNewData];
};

export const buildAssetsWithAssigned = (params: BuildAssetsWithAssignedParams): ShopifyAsset[] => {
  const { assignedData } = params;
  const processedAssets = processAssets(params);
  const updatedAssets = deepCopy(processedAssets);

  TAB_TEMPLATES.forEach(({ key: pageType }) => {
    const templateAssigned = assignedData?.[pageType];
    const { total: totalUnassigned, data: dataUnassigned } = processAssetUnassigned(
      templateAssigned || {},
      processedAssets,
    );

    const originIndex = updatedAssets.findIndex((asset) => {
      if (!asset.key) return false;

      const assetPageType = parseTemplateInfoBy({ templateSuffix: asset.key })?.type;
      return asset.providerSource === ProviderSource.SHOPIFY_ORIGIN && assetPageType === pageType;
    });

    if (originIndex !== -1) {
      const originAsset = updatedAssets[originIndex];
      updatedAssets[originIndex] = {
        ...originAsset,
        assigned: {
          url: originAsset.assigned?.url ?? '',
          total: (originAsset.assigned?.total ?? 0) + totalUnassigned,
          data: originAsset.assigned?.data
            ? mergeUniqueData(originAsset.assigned.data, dataUnassigned)
            : dataUnassigned,
        },
      };
    }

    // TODO: Current logic not support pick template default by GemPages
    // const defaultIndex = updatedAssets.findIndex((asset) => {
    //   if (!asset.key || asset.providerSource !== ProviderSource.GEMPAGESV7) return false;

    //   const templateId = defaultThemePages?.find((item) => item.type === pageType)?.id;
    //   const assetKeyId = parseTemplateIdBy(asset.key, ProviderSource.GEMPAGESV7);
    //   return templateId === assetKeyId;
    // });
    // if (defaultIndex !== -1) {
    //   const defaultAsset = updatedAssets[defaultIndex];
    //   updatedAssets[defaultIndex] = {
    //     ...defaultAsset,
    //     assigned: {
    //       url: defaultAsset.assigned?.url ?? '',
    //       total: (defaultAsset.assigned?.total ?? 0) + totalUnassigned,
    //       data: defaultAsset.assigned?.data
    //         ? mergeUniqueData(defaultAsset.assigned.data, dataUnassigned)
    //         : dataUnassigned,
    //     },
    //   };
    // }
  });

  return updatedAssets;
};
